import { ref, watch, computed, watchEffect } from 'vue';
import { injectLocal, provideLocal } from '@vueuse/core';

import { useBrand } from '~/composables/useBrand';
import { useUser } from '~/composables/user/useUser';
import { formatPrice } from '~/utils/commonUtils';
import { LOYALTY_INFO } from './constants/providers';
import { TIER_NAMES, MIN_SPEND_TO_UPGRADE, MAX_SPEND_TO_UPGRADE, POINTS_PER_DOLLAR_BY_TIER } from './constants/mcom';
const NON_LOYALIST_TIER_NAME = 'non-loyalist';

/**
 * Mcom Loyalty Data
 * @typedef {Object} McomLoyaltyData
 * @property {import('vue').ComputedRef<boolean>} isBronze
 * @property {import('vue').ComputedRef<boolean>} isSilver
 * @property {import('vue').ComputedRef<boolean>} isGold
 * @property {import('vue').ComputedRef<boolean>} isPlatinum
 * @property {import('vue').Ref<boolean>}  isUpgradeMessageAvailable
 * @property {import('vue').Ref<boolean>} isCardNotDefault
 * @property {import('vue').Ref<string>} nextTier
 * @property {import('vue').Ref<number>} spendToNextUpgrade
 * @property {import('vue').ComputedRef<number>} pointsPerDollar
 * @property {function(tierNameValue: string): number} getPointsPerDollarByTier
 * @property {function(userInfo: Object<bcomUserInfo, mcomUserInfo>)} updateUserInfoData
 */

/**
 * Handles mcom only related operations, and returns computed values and functions only used on mcom
 *
 * @param {import('vue').Ref<string>} tierName
 * @returns {McomLoyaltyData}
 * */
function useMcomLoyaltyProvide(tierName) {
  const nextTier = ref(TIER_NAMES.BRONZE);

  const isUpgradeMessageAvailable = ref(false);
  const isCardNotDefault = ref(false);
  const spendToNextUpgrade = ref('');

  const isBronze = computed(() => tierName.value === TIER_NAMES.BRONZE);
  const isSilver = computed(() => tierName.value === TIER_NAMES.SILVER);
  const isGold = computed(() => tierName.value === TIER_NAMES.GOLD);
  const isPlatinum = computed(() => tierName.value === TIER_NAMES.PLATINUM);

  const pointsPerDollar = computed(() => POINTS_PER_DOLLAR_BY_TIER[tierName.value]);

  /** @returns {boolean} * */
  function _isUpgradeMessageAvailable(userInfo) {
    const tierInfo = userInfo ? userInfo.tierInfo : null;
    if (!tierInfo) return false;

    const currentTier = tierName.value;
    const tiers = [TIER_NAMES.SILVER, TIER_NAMES.GOLD];

    if (tiers.includes(currentTier)) {
      const yearToDateSpend = Number(tierInfo.yearToDateSpend);
      return yearToDateSpend >= MIN_SPEND_TO_UPGRADE[currentTier] && yearToDateSpend < MAX_SPEND_TO_UPGRADE[currentTier];
    }
    return false;
  }

  /** @returns {string} * */
  function _getSpendToNextUpgrade(userInfo) {
    const { tierInfo } = userInfo || {};

    return tierInfo && formatPrice(tierInfo.spendToNextUpgrade);
  }

  /** @returns {boolean} * */
  function _isCardNotDefault(userInfo) {
    const { cardDefaultToWallet, isLtyIdLinked } = userInfo || {};
    return !!(!cardDefaultToWallet && isLtyIdLinked);
  }

  /** @param {Object<bcomUserInfo, mcomUserInfo>} userInfom */
  function updateUserInfoData(userInfo) {
    isUpgradeMessageAvailable.value = _isUpgradeMessageAvailable(userInfo);
    spendToNextUpgrade.value = _getSpendToNextUpgrade(userInfo);
    isCardNotDefault.value = _isCardNotDefault(userInfo);
  }

  /**
   * @param {string} tierNameValue
   * @returns {number}
   * */
  function getPointsPerDollarByTier(tierNameValue) {
    return POINTS_PER_DOLLAR_BY_TIER[tierNameValue] || 0;
  }

  watchEffect(() => {
    nextTier.value = {
      [NON_LOYALIST_TIER_NAME]: TIER_NAMES.BRONZE,
      [TIER_NAMES.BRONZE]: TIER_NAMES.SILVER,
      [TIER_NAMES.SILVER]: TIER_NAMES.GOLD,
      [TIER_NAMES.GOLD]: TIER_NAMES.PLATINUM,
      [TIER_NAMES.PLATINUM]: TIER_NAMES.PLATINUM,
    }[tierName.value];
  });

  return {
    isBronze,
    isSilver,
    isGold,
    isPlatinum,
    isUpgradeMessageAvailable,
    isCardNotDefault,
    nextTier,
    spendToNextUpgrade,
    pointsPerDollar,
    getPointsPerDollarByTier,
    updateUserInfoData,
  };
}

/**
 * Returns loyalty data, which optionally includes mcom loyalty data
 *
 *  @returns {Object<
 *  {
 *   tierName: import('vue').Ref<string>,
 *   isUserNonLoyalist: import('vue').ComputedRef<boolean>,
 *   defaultTenderType: import('vue').Ref<string>,
 *   encryptedLoyaltyId: import('vue').Ref<string>,
 *  },
 *  Partial<Omit<McomLoyaltyData, 'updateUserInfoData'>>
 * >}
 */
export const useLoyalty = () => {
  const loyaltyData = injectLocal(LOYALTY_INFO);

  if (typeof loyaltyData === 'undefined') {
    throw new TypeError('useLoyalty() is called without provider.');
  }

  return loyaltyData;
};

export function useLoyaltyProvide() {
  const tierName = ref(NON_LOYALIST_TIER_NAME);

  const { isMcom } = useBrand();
  const { userInfo } = useUser();
  const { updateUserInfoData: _updateUserInfoData, ...mcomLoyaltyData } = isMcom ? useMcomLoyaltyProvide(tierName) : {};

  const isUserNonLoyalist = computed(() => tierName.value === NON_LOYALIST_TIER_NAME);

  const defaultTenderType = ref('V');
  const encryptedLoyaltyId = ref('');
  const isLoyalistInfoEmpty = ref(true);

  /** @returns {string} * */
  function _getTierName() {
    if (!userInfo.value) return NON_LOYALIST_TIER_NAME;

    const { tierInfo } = userInfo.value;
    const userTierName = tierInfo && tierInfo.tierName;

    return userTierName?.toLowerCase() || NON_LOYALIST_TIER_NAME;
  }

  /** @returns {string} */
  function _getDefaultTenderType() {
    return (userInfo.value && userInfo.value.defaultTenderType) || 'V';
  }

  /** @returns {string} */
  function _getEncryptedLoyaltyId() {
    return (userInfo.value && userInfo.value.encryptedLoyaltyId) || '';
  }

  watch(
    userInfo,
    (newUserInfo) => {
      tierName.value = _getTierName();
      defaultTenderType.value = _getDefaultTenderType();
      encryptedLoyaltyId.value = _getEncryptedLoyaltyId();
      isLoyalistInfoEmpty.value = Object.keys(newUserInfo?.tierInfo || {}).length === 0;
      _updateUserInfoData?.(newUserInfo);
    },
    { deep: true },
  );

  // provides common loyalty data
  provideLocal(LOYALTY_INFO, {
    tierName,
    isUserNonLoyalist,
    defaultTenderType,
    encryptedLoyaltyId,
    isLoyalistInfoEmpty,
    ...mcomLoyaltyData,
  });
}
